import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, MenuItem, TextField, Button, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, IconButton, Tooltip, CircularProgress, Chip,
} from '@mui/material';
import { Home, Print, Leaderboard, Search, Download } from '@mui/icons-material';
import axios from 'axios';
import ExamSelect, { useTermExams, examParam } from './ExamSelect';
import { useCompany } from '../hooks/useCompany';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const num = (n, d = 2) => (n == null ? '—' : Number(n).toFixed(d));

// A grade-distribution table (Grade Breakdown / Gender / per-Subject). `rows` are
// { label, counts:{code:n}, x, y, entries, meanMarks, meanPoints, grade }.
function SummaryTable({ title, firstCol, gradeCodes, rows }) {
  if (!rows || rows.length === 0) return null;
  const cell = (n) => (n ? n : '');   // blank instead of 0, like the printed report
  return (
    <Box sx={{ mt: 3, breakInside: 'avoid' }}>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5, letterSpacing: 0.3 }}>{title}</Typography>
      <Table size="small" sx={{ '& td, & th': { px: 0.6, py: 0.3, fontSize: 11, whiteSpace: 'nowrap', textAlign: 'center' } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ textAlign: 'left !important' }}><strong>{firstCol}</strong></TableCell>
            {gradeCodes.map(c => <TableCell key={c}><strong>{c}</strong></TableCell>)}
            <TableCell><strong>X</strong></TableCell>
            <TableCell><strong>Y</strong></TableCell>
            <TableCell><strong>ENTRIES</strong></TableCell>
            <TableCell><strong>MEAN MARKS</strong></TableCell>
            <TableCell><strong>MEAN PTS</strong></TableCell>
            <TableCell><strong>GRADE</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.label || i} hover>
              <TableCell sx={{ textAlign: 'left !important', fontWeight: 600 }}>{r.label}</TableCell>
              {gradeCodes.map(c => <TableCell key={c}>{cell(r.counts?.[c])}</TableCell>)}
              <TableCell>{cell(r.x)}</TableCell>
              <TableCell>{cell(r.y)}</TableCell>
              <TableCell><strong>{r.entries}</strong></TableCell>
              <TableCell>{num(r.meanMarks, 1)}</TableCell>
              <TableCell>{num(r.meanPoints, 2)}</TableCell>
              <TableCell><strong>{r.grade || '—'}</strong></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function MeritList() {
  const navigate = useNavigate();
  const company = useCompany();
  const [years, setYears]     = useState([]);
  const [stages, setStages]   = useState([]);
  const [streams, setStreams] = useState([]);
  const [f, setF] = useState({ academicYear: '', term: '1', stageCode: '', streamCode: '', exam: '' });
  const exams = useTermExams(f.academicYear, f.term, f.stageCode);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    axios.get(`${API}/api/academics/years`, { headers: hdrs() })
      .then(r => { setYears(r.data || []); const active = (r.data || []).find(y => y.isActive); if (active) setF(s => ({ ...s, academicYear: active.year })); })
      .catch(() => {});
    // Default Year + Term to the current academic period (active year + term in
    // session). Term options are string-valued, so stringify. Falls back to the
    // year-based default above if the period can't be resolved.
    axios.get(`${API}/api/academics/current-period`, { headers: hdrs() })
      .then(r => setF(s => ({
        ...s,
        academicYear: r.data?.year || s.academicYear,
        term: r.data?.term ? String(r.data.term) : s.term,
      })))
      .catch(() => {});
    axios.get(`${API}/api/stages`, { headers: hdrs() }).then(r => setStages(r.data || [])).catch(() => {});
    axios.get(`${API}/api/academics/streams`, { headers: hdrs() }).then(r => setStreams(r.data || [])).catch(() => {});
    // Class teachers: prefill grade/stream from their assigned class
    const role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
    if (role === 'classTeacher') {
      axios.get(`${API}/api/academics/class-teacher/classes`, { headers: hdrs() })
        .then(r => {
          const cls = (r.data || [])[0];
          if (cls) setF(s => ({ ...s, stageCode: cls.stageCode, streamCode: cls.streamCode || '' }));
        })
        .catch(() => {});
    }
  }, []);

  const run = useCallback(async () => {
    if (!f.academicYear || !f.term || !f.stageCode) { setError('Select academic year, term and grade/stage'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const params = { academicYear: f.academicYear, term: f.term, stageCode: f.stageCode };
      if (f.streamCode) params.streamCode = f.streamCode;
      const examId = examParam(exams, f.exam);   // none = termly weighted average
      if (examId) params.exam = examId;
      const r = await axios.get(`${API}/api/academics/assessment-scores/merit-list`, { headers: hdrs(), params });
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load merit list');
    } finally { setLoading(false); }
  }, [f, exams]);

  // Auto-generate as soon as the selection is complete (Year + Term + Grade/Stage),
  // and re-run whenever Year, Term, Grade/Stage or Stream changes — no need to
  // click Generate. The button remains for an explicit re-run.
  useEffect(() => {
    if (!f.academicYear || !f.term || !f.stageCode) return;
    run();
  }, [f.academicYear, f.term, f.stageCode, f.streamCode, f.exam, exams]); // eslint-disable-line react-hooks/exhaustive-deps

  const subjects = data?.subjects || [];
  const stageLabel = stages.find(s => s.code === f.stageCode)?.name || f.stageCode;
  // Stream columns (STR / STR POS) only make sense when the stage actually runs
  // streams; otherwise they just echo the class name and the overall position.
  const showStreams = !!data?.hasStreams;

  // Stream filter options: configured Stream records unioned with whatever the last
  // merit-list returned (which includes classes-acting-as-streams when no stream is set).
  const streamOpts = useMemo(() => {
    const m = {};
    streams.forEach(s => { m[s.streamCode] = s.streamName || s.streamCode; });
    (data?.streamOptions || []).forEach(o => { m[o.code] = o.name || o.code; });
    return Object.entries(m).map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [streams, data]);

  // Export to Excel with landscape orientation
  const exportToExcel = () => {
    if (!data) return;

    const ws = XLSX.utils.aoa_to_sheet([]);

    // Title rows
    ws['!rows'] = [{ hpt: 20 }];
    XLSX.utils.sheet_add_aoa(ws, [[company?.name || 'School']], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [[`Merit List — ${stageLabel} · Term ${data.term} · ${data.academicYear}${data.streamCode ? ` · ${streamOpts.find(s => s.code === data.streamCode)?.name || data.streamCode}` : ''}`]], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(ws, [[`${data.count} learners · Class Mean ${num(data.classMean)}`]], { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(ws, [['']]);

    // Headers
    const headers = ['#', 'ADMNO', 'NAME', ...(showStreams ? ['STR'] : []), ...subjects.map(s => s.code), 'SBJ', 'TT', 'MEAN', 'DEV', 'MG', 'TT PTS', 'MN PTS', ...(showStreams ? ['STR POS'] : []), 'OVR POS'];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A5' });

    // Data rows
    const rows = data.rows.map((r, i) => [
      i + 1,
      r.admNo,
      r.name,
      ...(showStreams ? [r.streamName] : []),
      ...r.subjects.map(s => (s.score == null ? '' : `${s.score} ${s.grade}`)),
      r.sbj,
      r.totalMarks,
      r.mean,
      r.dev == null ? '' : r.dev,
      r.pl,
      r.totalPoints,
      r.meanPoints,
      ...(showStreams ? [r.streamPos] : []),
      r.overallPos
    ]);
    XLSX.utils.sheet_add_aoa(ws, rows, { origin: 'A6' });

    // Format: Set column widths
    ws['!cols'] = [
      { wch: 4 },   // #
      { wch: 10 },  // ADMNO
      { wch: 18 },  // NAME
      ...(showStreams ? [{ wch: 12 }] : []),   // STR
      ...subjects.map(() => ({ wch: 8 })),
      { wch: 5 },   // SBJ
      { wch: 8 },   // TT
      { wch: 8 },   // MEAN
      { wch: 8 },   // DEV
      { wch: 5 },   // MG
      { wch: 8 },   // TT PTS
      { wch: 8 },   // MN PTS
      ...(showStreams ? [{ wch: 8 }] : []),    // STR POS
      { wch: 8 }    // OVR POS
    ];

    // Create workbook with landscape orientation
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Merit List');

    // Set page orientation to landscape
    if (!wb.Workbook) wb.Workbook = {};
    if (!wb.Workbook.Sheets) wb.Workbook.Sheets = [];
    wb.Workbook.Sheets[0] = {
      pageSetup: { orientation: 'landscape', paperSize: 9, scale: 80, fitToPage: true, fitToHeight: 1, fitToWidth: 1 },
      pageMargins: { left: 0.5, right: 0.5, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 }
    };

    XLSX.writeFile(wb, `Merit-List_${f.academicYear}_T${f.term}_${f.stageCode}${f.streamCode ? `_${f.streamCode}` : ''}.xlsx`);
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 3, mb: 4, px: 2 }}>
      <style>{`
        @page { size: A4 landscape; margin: 0.5in; }
        @media print {
          .no-print { display:none !important; }
          #ml-print, #ml-print * { visibility:visible; }
          body * { visibility:hidden; }
          #ml-print { position:absolute; left:0; top:0; width:100%; }
        }
      `}</style>

      {/* Controls */}
      <Card elevation={3} className="no-print" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Leaderboard color="primary" />
              <Typography variant="h5" color="primary">Merit List</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Export to Excel"><span><IconButton onClick={exportToExcel} disabled={!data}><Download /></IconButton></span></Tooltip>
              <Tooltip title="Print"><span><IconButton onClick={() => window.print()} disabled={!data}><Print /></IconButton></span></Tooltip>
              <Tooltip title="Academics"><IconButton onClick={() => navigate('/academics')}><Home /></IconButton></Tooltip>
            </Box>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <TextField select size="small" label="Academic Year" sx={{ minWidth: 130 }}
              value={f.academicYear} onChange={e => setF(s => ({ ...s, academicYear: e.target.value }))}>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}{y.isActive ? ' (Active)' : ''}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Term" sx={{ width: 110 }}
              value={f.term} onChange={e => setF(s => ({ ...s, term: e.target.value }))}>
              {[1, 2, 3].map(t => <MenuItem key={t} value={String(t)}>Term {t}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Grade / Stage" sx={{ minWidth: 150 }}
              value={f.stageCode} onChange={e => setF(s => ({ ...s, stageCode: e.target.value }))}>
              {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.name || s.code}</MenuItem>)}
            </TextField>
            <ExamSelect exams={exams} value={f.exam} onChange={v => setF(s => ({ ...s, exam: v }))} />
            <TextField select size="small" label="Stream (all)" sx={{ minWidth: 140 }}
              value={f.streamCode} onChange={e => setF(s => ({ ...s, streamCode: e.target.value }))}>
              <MenuItem value=""><em>All streams</em></MenuItem>
              {streamOpts.map(s => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
            </TextField>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              onClick={run} disabled={loading}>{loading ? 'Loading…' : 'Generate'}</Button>
          </Box>
        </CardContent>
      </Card>

      {/* Report */}
      {data && (
        <Card elevation={2} id="ml-print">
          <CardContent sx={{ overflowX: 'auto' }}>
            <Box textAlign="center" mb={1.5}>
              <Typography variant="h6" fontWeight={800}>{company?.name || 'School'}</Typography>
              <Typography variant="subtitle2">
                Merit List — {stageLabel} · Term {data.term} · {data.academicYear}
                {data.streamCode ? ` · ${streamOpts.find(s => s.code === data.streamCode)?.name || data.streamCode}` : ''}
              </Typography>
              <Chip size="small" sx={{ mt: 0.5 }} label={`${data.count} learners · Class Mean ${num(data.classMean)}`} />
            </Box>

            <Table size="small" sx={{ '& td, & th': { px: 0.75, py: 0.4, fontSize: 11.5, whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>ADMNO</strong></TableCell>
                  <TableCell><strong>NAME</strong></TableCell>
                  {showStreams && <TableCell><strong>STR</strong></TableCell>}
                  {subjects.map(s => <TableCell key={s.code} align="center"><strong>{s.code}</strong></TableCell>)}
                  <TableCell align="center"><strong>SBJ</strong></TableCell>
                  <TableCell align="right"><strong>TT</strong></TableCell>
                  <TableCell align="right"><strong>MEAN</strong></TableCell>
                  <TableCell align="right"><strong>DEV</strong></TableCell>
                  <TableCell align="center"><strong>MG</strong></TableCell>
                  <TableCell align="right"><strong>TT PTS</strong></TableCell>
                  <TableCell align="right"><strong>MN PTS</strong></TableCell>
                  {showStreams && <TableCell align="center"><strong>STR POS</strong></TableCell>}
                  <TableCell align="center"><strong>OVR POS</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={r.admNo} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{r.admNo}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.name}</TableCell>
                    {showStreams && <TableCell>{r.streamName}</TableCell>}
                    {r.subjects.map(s => (
                      <TableCell key={s.code} align="center">
                        {s.score == null ? '—' : <>{s.score} <span style={{ color: '#607d8b', fontSize: 10 }}>{s.grade}</span></>}
                      </TableCell>
                    ))}
                    <TableCell align="center">{r.sbj}</TableCell>
                    <TableCell align="right"><strong>{num(r.totalMarks, 0)}</strong></TableCell>
                    <TableCell align="right">{num(r.mean)}</TableCell>
                    <TableCell align="right" sx={{ color: r.dev == null ? 'text.secondary' : (r.dev >= 0 ? 'success.main' : 'error.main') }}>
                      {r.dev == null ? '—' : (r.dev > 0 ? `+${num(r.dev, 1)}` : num(r.dev, 1))}
                    </TableCell>
                    <TableCell align="center">{r.pl}</TableCell>
                    <TableCell align="right">{num(r.totalPoints, 0)}</TableCell>
                    <TableCell align="right">{num(r.meanPoints, 4)}</TableCell>
                    {showStreams && <TableCell align="center">{r.streamPos}</TableCell>}
                    <TableCell align="center"><strong>{r.overallPos}</strong></TableCell>
                  </TableRow>
                ))}
                {data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={subjects.length + (showStreams ? 13 : 11)} align="center">
                    <Typography color="text.secondary" py={2}>No scored learners for this selection.</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>

            {/* ── Summary blocks (match the printed merit list) ── */}
            {data.summaries && (
              <>
                <SummaryTable
                  title="GRADE BREAKDOWN"
                  firstCol="FORM"
                  gradeCodes={data.gradeCodes || []}
                  rows={[{ ...data.summaries.gradeBreakdown, label: stageLabel }]}
                />
                <SummaryTable
                  title="GENDER GRADE SUMMARY"
                  firstCol="GENDER"
                  gradeCodes={data.gradeCodes || []}
                  rows={data.summaries.gender || []}
                />
                <SummaryTable
                  title="CLASS GRADE SUMMARY"
                  firstCol="SUBJECT"
                  gradeCodes={data.gradeCodes || []}
                  rows={(data.summaries.subjects || []).map(s => ({ ...s, label: s.name || s.code }))}
                />
                {(data.summaries.minLearningAreas > 0) && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    X = fewer than {data.summaries.minLearningAreas} subjects (incomplete entry, unranked) · Y = exam irregularity
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
